import React, { useState, useEffect } from 'react';
import { getVotacionByAnuncio, votar, crearVotacion, cerrarVotacion } from '../services/api';
import { useToast } from './Toast';

function Votacion({ anuncioId, puedeGestionar }) {
  const toast = useToast();
  const [votacion, setVotacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [showCrearModal, setShowCrearModal] = useState(false);
  const [nuevaVotacion, setNuevaVotacion] = useState({
    pregunta: '',
    tipo_votacion: 'simple',
    max_opciones: 1,
    opciones: ['', '']
  });

  useEffect(() => {
    cargarVotacion();
  }, [anuncioId]);

  const cargarVotacion = async () => {
    try {
      const response = await getVotacionByAnuncio(anuncioId);
      setVotacion(response.data);
      if (response.data && response.data.votos_usuario) {
        setSeleccionadas(response.data.votos_usuario);
      }
    } catch (error) {
      console.error('Error al cargar votación:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeleccionar = (opcionId) => {
    if (votacion.tipo_votacion === 'simple') {
      setSeleccionadas([opcionId]);
    } else {
      if (seleccionadas.includes(opcionId)) {
        setSeleccionadas(seleccionadas.filter(id => id !== opcionId));
      } else {
        if (seleccionadas.length < votacion.max_opciones) {
          setSeleccionadas([...seleccionadas, opcionId]);
        } else {
          toast.error(`Máximo ${votacion.max_opciones} opciones`);
        }
      }
    }
  };

  const handleVotar = async () => {
    if (seleccionadas.length === 0) {
      toast.error('Selecciona al menos una opción');
      return;
    }

    setEnviando(true);
    try {
      await votar({
        votacion_id: votacion.id,
        opciones_ids: seleccionadas
      });
      toast.success('Voto registrado correctamente');
      cargarVotacion();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al votar');
    } finally {
      setEnviando(false);
    }
  };

  const handleCrearVotacion = async (e) => {
    e.preventDefault();
    
    const opcionesLimpias = nuevaVotacion.opciones.filter(o => o.trim());
    if (opcionesLimpias.length < 2) {
      toast.error('Debes añadir al menos 2 opciones');
      return;
    }

    try {
      await crearVotacion({
        anuncio_id: anuncioId,
        pregunta: nuevaVotacion.pregunta,
        tipo_votacion: nuevaVotacion.tipo_votacion,
        max_opciones: nuevaVotacion.max_opciones,
        opciones: opcionesLimpias
      });
      toast.success('Votación creada correctamente');
      setShowCrearModal(false);
      cargarVotacion();
    } catch (error) {
      toast.error('Error al crear votación');
    }
  };

  const handleCerrarVotacion = async () => {
    if (!window.confirm('¿Cerrar la votación? Los usuarios no podrán votar más.')) return;

    try {
      await cerrarVotacion(votacion.id);
      toast.success('Votación cerrada');
      cargarVotacion();
    } catch (error) {
      toast.error('Error al cerrar votación');
    }
  };

  const agregarOpcion = () => {
    setNuevaVotacion({
      ...nuevaVotacion,
      opciones: [...nuevaVotacion.opciones, '']
    });
  };

  const eliminarOpcion = (index) => {
    if (nuevaVotacion.opciones.length <= 2) return;
    const nuevasOpciones = nuevaVotacion.opciones.filter((_, i) => i !== index);
    setNuevaVotacion({ ...nuevaVotacion, opciones: nuevasOpciones });
  };

  const actualizarOpcion = (index, valor) => {
    const nuevasOpciones = [...nuevaVotacion.opciones];
    nuevasOpciones[index] = valor;
    setNuevaVotacion({ ...nuevaVotacion, opciones: nuevasOpciones });
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Cargando votación...</div>;
  }

  if (!votacion && !puedeGestionar) {
    return null;
  }

  if (!votacion && puedeGestionar) {
    return (
      <div className="card" style={{ marginTop: '20px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>📊 Votación/Encuesta</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
          No hay votación activa en este anuncio
        </p>
        <button onClick={() => setShowCrearModal(true)} className="btn btn-primary btn-sm">
          + Crear Votación
        </button>

        {showCrearModal && (
          <div className="modal-overlay" onClick={() => setShowCrearModal(false)}>
            <div className="modal-content" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
              <h2 style={{ marginBottom: '24px' }}>Crear Votación</h2>
              <form onSubmit={handleCrearVotacion}>
                <div className="form-group">
                  <label>Pregunta *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={nuevaVotacion.pregunta}
                    onChange={(e) => setNuevaVotacion({ ...nuevaVotacion, pregunta: e.target.value })}
                    required
                    placeholder="¿Cuál es tu preferencia?"
                  />
                </div>

                <div className="form-group">
                  <label>Tipo de votación</label>
                  <select
                    className="form-control"
                    value={nuevaVotacion.tipo_votacion}
                    onChange={(e) => setNuevaVotacion({ 
                      ...nuevaVotacion, 
                      tipo_votacion: e.target.value,
                      max_opciones: e.target.value === 'simple' ? 1 : 2
                    })}
                  >
                    <option value="simple">Opción única</option>
                    <option value="multiple">Múltiples opciones</option>
                  </select>
                </div>

                {nuevaVotacion.tipo_votacion === 'multiple' && (
                  <div className="form-group">
                    <label>Máximo de opciones a seleccionar</label>
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      value={nuevaVotacion.max_opciones}
                      onChange={(e) => setNuevaVotacion({ ...nuevaVotacion, max_opciones: parseInt(e.target.value) })}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Opciones *</label>
                  {nuevaVotacion.opciones.map((opcion, index) => (
                    <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <input
                        type="text"
                        className="form-control"
                        value={opcion}
                        onChange={(e) => actualizarOpcion(index, e.target.value)}
                        placeholder={`Opción ${index + 1}`}
                        required
                      />
                      {nuevaVotacion.opciones.length > 2 && (
                        <button
                          type="button"
                          onClick={() => eliminarOpcion(index)}
                          className="btn btn-sm btn-danger"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={agregarOpcion} className="btn btn-sm btn-outline" style={{ marginTop: '8px' }}>
                    + Añadir opción
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button type="submit" className="btn btn-primary">Crear Votación</button>
                  <button type="button" onClick={() => setShowCrearModal(false)} className="btn btn-outline">
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Votación existente
  const totalVotos = votacion.opciones.reduce((sum, op) => sum + op.votos, 0);

  return (
    <div className="card" style={{ marginTop: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600' }}>
          📊 {votacion.pregunta}
        </h3>
        {puedeGestionar && votacion.activa && (
          <button onClick={handleCerrarVotacion} className="btn btn-sm btn-danger">
            Cerrar Votación
          </button>
        )}
      </div>

      {!votacion.activa && (
        <div style={{ 
          padding: '8px 12px', 
          background: 'var(--light-color)', 
          borderRadius: '6px', 
          marginBottom: '12px',
          fontSize: '14px',
          color: 'var(--text-secondary)'
        }}>
          🔒 Votación cerrada
        </div>
      )}

      <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
        {votacion.tipo_votacion === 'multiple' 
          ? `Selecciona hasta ${votacion.max_opciones} opciones` 
          : 'Selecciona una opción'}
        {' '}· {votacion.total_votantes} {votacion.total_votantes === 1 ? 'persona ha votado' : 'personas han votado'}
      </p>

      <div style={{ marginBottom: '16px' }}>
        {votacion.opciones.map((opcion) => {
          const porcentaje = totalVotos > 0 ? ((opcion.votos / totalVotos) * 100).toFixed(1) : 0;
          const estaSeleccionada = seleccionadas.includes(opcion.id);

          return (
            <div
              key={opcion.id}
              onClick={() => votacion.activa && !votacion.usuario_ha_votado && handleSeleccionar(opcion.id)}
              style={{
                padding: '12px',
                marginBottom: '8px',
                border: `2px solid ${estaSeleccionada ? 'var(--primary-color)' : 'var(--border-color)'}`,
                borderRadius: '8px',
                cursor: (votacion.activa && !votacion.usuario_ha_votado) ? 'pointer' : 'default',
                backgroundColor: estaSeleccionada ? 'var(--primary-color)' + '10' : 'transparent',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ fontWeight: '500' }}>{opcion.texto}</span>
                  <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    {opcion.votos} votos ({porcentaje}%)
                  </span>
                </div>
                <div style={{ 
                  height: '6px', 
                  background: 'var(--light-color)', 
                  borderRadius: '3px', 
                  overflow: 'hidden' 
                }}>
                  <div style={{ 
                    height: '100%', 
                    background: 'var(--primary-color)', 
                    width: `${porcentaje}%`,
                    transition: 'width 0.3s'
                  }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {votacion.activa && !votacion.usuario_ha_votado && (
        <button
          onClick={handleVotar}
          disabled={seleccionadas.length === 0 || enviando}
          className="btn btn-primary"
          style={{ width: '100%' }}
        >
          {enviando ? 'Enviando...' : 'Enviar Voto'}
        </button>
      )}

      {votacion.usuario_ha_votado && (
        <div style={{ 
          padding: '12px', 
          background: 'var(--success-color)' + '15', 
          border: '1px solid var(--success-color)',
          borderRadius: '8px',
          textAlign: 'center',
          color: 'var(--success-color)',
          fontWeight: '500'
        }}>
          ✓ Ya has votado en esta encuesta
        </div>
      )}
    </div>
  );
}

export default Votacion;
